/**
 * <RoleHomeRedirect> — chooses the landing page for the authenticated
 * user, branching by `accessLevel` (Phase 5 W3.T3.2 / Q01–Q05).
 *
 * Routing matrix (PLAN T3.2 step 1):
 *   - ADMIN, MANAGER → /dashboard (new in Phase 5)
 *   - SUPERVISOR     → /supervisor (Phase 2 settings console)
 *   - EMPLOYEE       → /workload (default — only operational surface)
 *
 * **Loading-flicker fix (UI S-10 / round-2):** while `useMe()` is still
 * resolving (`me === undefined`) we render `null` instead of a default
 * `<Navigate to="/workload">`. The previous shape produced an
 * intermediate `/workload` URL frame for ADMIN/MANAGER cold-loads — the
 * URL bar would flash `/workload`, then the resolved `me` would re-fire
 * `<Navigate to="/dashboard">`. Returning `null` is safe because:
 *   - the parent `<RequireAuth>` already shows a skeleton during the
 *     same `isLoading` window, so the user sees the shell, not a blank
 *     screen;
 *   - on the rare race where `<RequireAuth>` resolves but `useMe()` is
 *     still cached-stale (`undefined` from a fresh QueryClient hydration),
 *     `null` is a one-paint hold, not a navigation event — no history
 *     entry is wasted.
 *
 * `me === null` (user actually unauthenticated — RequireAuth missed) →
 * redirect to `/login`. This is defensive; the outer guard should make
 * this branch unreachable, but a misuse of this component outside the
 * `<RequireAuth>` envelope must not silently land on the wrong home.
 */
import * as React from 'react';
import { Navigate } from 'react-router-dom';

import { isMeHydrating, useMe } from '@/hooks/use-me';

export function RoleHomeRedirect(): React.ReactElement | null {
  const { data: me } = useMe();

  // Round-2 / UI S-10: loading → render nothing (no `/workload` flash).
  if (me === undefined) return null;

  // RequireAuth should already have redirected; defensive fall-through.
  if (!me) return <Navigate to="/login" replace />;

  // Hard-nav fix (2026-06-10, fix-pack-2): hold while `me` is present but
  // not yet carrying `accessLevel`. Without this, the switch below hits
  // `default` and fires a COMMITTED `<Navigate replace to="/workload">` —
  // stranding a SUPERVISOR / ADMIN / MANAGER on the wrong home with no
  // re-evaluation once the real level arrives (the QA "post-login lands on
  // /workload" LOW, same root cause). Returning `null` is a one-paint hold
  // (the parent <RequireAuth> shows the shell); `/auth/me` then replaces the
  // partial object and the switch routes each tier to its correct home.
  // With the BE fix-pack-2 the partial shape no longer reaches the cache,
  // so this is regression armor that also closes the historical window.
  if (isMeHydrating(me)) return null;

  switch (me.accessLevel) {
    case 'ADMIN':
    case 'MANAGER':
      return <Navigate to="/dashboard" replace />;
    case 'SUPERVISOR':
      return <Navigate to="/supervisor" replace />;
    case 'EMPLOYEE':
    default:
      return <Navigate to="/workload" replace />;
  }
}
